import React, { useState, useMemo } from 'react';
import { colors } from '../../utils/colors';
import { MOCK_CLASSES, getStudentsForClass } from '../../utils/mockData';
import type { Teacher, Student, AttendanceStatus, ClassInfo } from '../../utils/types';
import SlideToSubmit from '../../components/SlideToSubmit';

interface Props {
  teacher: Teacher;
  onBack: () => void;
}

type View = 'class-select' | 'marking';

const formatShort = () =>
  new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'short' });

const STATUSES: { value: NonNullable<AttendanceStatus>; label: string; short: string; color: string; bg: string; border: string; pill: string }[] = [
  { value: 'present', label: 'Present', short: 'P', color: '#166534', bg: '#f0fdf4', border: '#86efac', pill: '#dcfce7' },
  { value: 'absent',  label: 'Absent',  short: 'A', color: '#991b1b', bg: '#fff1f2', border: '#fca5a5', pill: '#fee2e2' },
  { value: 'late',    label: 'Late',    short: 'L', color: '#92400e', bg: '#fffbeb', border: '#fcd34d', pill: '#fef3c7' },
];

function getStatusCfg(s: AttendanceStatus) {
  return STATUSES.find(x => x.value === s) ?? null;
}

const StudentRow: React.FC<{
  student: Student;
  index: number;
  onChange: (id: string, status: AttendanceStatus) => void;
  onRollClick: (student: Student) => void;
}> = ({ student, index, onChange, onRollClick }) => {
  const cfg = getStatusCfg(student.attendance ?? null);
  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${colors.borderLight}`,
      borderRadius: 14, padding: '10px 14px',
      display: 'flex', alignItems: 'center', gap: 12,
      animation: `slideUp 0.2s ease ${Math.min(index * 0.025, 0.25)}s both`,
    }}>
      <div onClick={() => onRollClick(student)} style={{
        width: 40, height: 40, borderRadius: '50%',
        background: colors.primaryBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, fontSize: 13, fontWeight: 800, color: colors.primary,
        cursor: 'pointer',
      }}>
        {student.rollNo}
      </div>
      <div style={{
        flex: 1, fontSize: 14, fontWeight: 600, color: cfg ? cfg.color : colors.text,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {student.name}
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {STATUSES.map(s => {
          const isActive = student.attendance === s.value;
          return (
            <button key={s.value} onClick={() => onChange(student.id, isActive ? null : s.value)} style={{
              width: 42, height: 42, borderRadius: 10,
              border: `1.5px solid ${isActive ? s.border : colors.borderLight}`,
              background: isActive ? s.bg : '#fff',
              color: isActive ? s.color : colors.textLight,
              cursor: 'pointer', fontSize: 13, fontWeight: 800,
              transition: 'all 0.12s',
            }}>
              {s.short}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const AttendanceDrawer: React.FC<{
  student: Student;
  status: AttendanceStatus;
  onStatusChange: (s: AttendanceStatus) => void;
  onSave: () => void;
  onClose: () => void;
}> = ({ student, status, onStatusChange, onSave, onClose }) => {
  const cfg = getStatusCfg(student.attendance ?? null);
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
        zIndex: 200, animation: 'fadeIn 0.2s ease both',
      }}/>
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: '#fff', borderRadius: '22px 22px 0 0',
        padding: '0 20px 36px', zIndex: 201,
        animation: 'sheetSlideUp 0.3s cubic-bezier(.32,.72,0,1) both',
        maxHeight: '80vh', overflowY: 'auto',
      }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: colors.borderLight, margin: '14px auto 22px' }}/>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          background: cfg ? cfg.bg : colors.primaryBg,
          border: `1.5px solid ${cfg ? cfg.border : colors.borderLight}`,
          borderRadius: 14, padding: '14px 16px', marginBottom: 20,
        }}>
          <div style={{
            width: 46, height: 46, borderRadius: '50%',
            background: colors.primaryBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 800, color: colors.primary, flexShrink: 0,
          }}>
            {student.rollNo}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>{student.name}</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
              Roll No. {student.rollNo}
              {cfg && <span style={{ color: cfg.color, fontWeight: 600 }}> · {cfg.label}</span>}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.7px', marginBottom: 10 }}>
          SELECT STATUS
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {STATUSES.map(s => {
            const active = status === s.value;
            return (
              <button key={s.value} onClick={() => onStatusChange(s.value)} style={{
                padding: '14px 16px', borderRadius: 13,
                border: `2px solid ${active ? s.border : colors.border}`,
                background: active ? s.bg : '#fff',
                display: 'flex', alignItems: 'center', gap: 14,
                cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 9,
                  background: active ? s.bg : '#f8fafc',
                  border: `1.5px solid ${active ? s.border : colors.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 800, color: active ? s.color : colors.textLight,
                }}>
                  {s.short}
                </div>
                <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: active ? s.color : colors.text }}>
                  {s.label}
                </span>
                {active && (
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', background: s.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <button onClick={onSave} style={{
          width: '100%', padding: '15px',
          background: status ? colors.primaryDark : colors.borderLight,
          color: status ? '#fff' : colors.textLight,
          border: 'none', borderRadius: 14,
          fontSize: 15, fontWeight: 700,
          cursor: status ? 'pointer' : 'not-allowed',
        }}>
          Save Changes
        </button>
      </div>
    </>
  );
};

const ClassAttendance: React.FC<Props> = ({ teacher, onBack }) => {
  const [view, setView] = useState<View>('class-select');
  const [selectedClass, setSelectedClass] = useState<ClassInfo | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [drawerStudent, setDrawerStudent] = useState<Student | null>(null);
  const [drawerStatus, setDrawerStatus] = useState<AttendanceStatus>(null);

  const openDrawer  = (s: Student) => { setDrawerStudent(s); setDrawerStatus(s.attendance ?? null); };
  const closeDrawer = () => setDrawerStudent(null);
  const saveDrawer  = () => {
    if (!drawerStudent) return;
    setStudents(prev => prev.map(s => s.id === drawerStudent.id ? { ...s, attendance: drawerStatus } : s));
    closeDrawer();
  };

  const handleClassSelect = (cls: ClassInfo) => {
    setSelectedClass(cls);
    setStudents(getStudentsForClass(cls.id));
    setView('marking');
  };

  const handleStatusChange = (id: string, status: AttendanceStatus) =>
    setStudents(prev => prev.map(s => s.id === id ? { ...s, attendance: status } : s));

  const handleSubmit = async () => {
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 800));
    setSubmitting(false);
    onBack();
  };

  const counts = useMemo(() => ({
    present:  students.filter(s => s.attendance === 'present').length,
    absent:   students.filter(s => s.attendance === 'absent').length,
    late:     students.filter(s => s.attendance === 'late').length,
    unmarked: students.filter(s => !s.attendance).length,
  }), [students]);

  if (view === 'marking' && selectedClass) {
    return (
      <div style={{
        height: '100dvh', background: '#FDFCFE',
        display: 'flex', flexDirection: 'column',
        fontFamily: "'Inter', sans-serif",
        position: 'relative',
      }}>
        {/* Header */}
        <div style={{
          padding: '52px 20px 14px',
          display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: `1px solid ${colors.borderLight}`,
          background: '#fff', flexShrink: 0, zIndex: 10,
        }}>
          <button onClick={() => setView('class-select')} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 16, fontWeight: 500, color: colors.text,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Student attendance
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 130px' }}>
          <div style={{
            background: colors.primaryDark, borderRadius: 12,
            padding: '12px 18px', marginBottom: 14,
          }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{selectedClass.name}</span>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginLeft: 6 }}>- {formatShort()}</span>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <div style={{
              flex: 1, background: colors.primaryBg,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 12, padding: '10px 8px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: colors.primary }}>{students.length}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.5px', marginTop: 2 }}>TOTAL</div>
            </div>
            {[
              { label: 'PRESENT', value: counts.present, color: '#166534', bg: '#f0fdf4', border: '#86efac' },
              { label: 'LATE',    value: counts.late,    color: '#92400e', bg: '#fffbeb', border: '#fcd34d' },
              { label: 'ABSENT',  value: counts.absent,  color: '#0e7490', bg: '#ecfeff', border: '#67e8f9' },
            ].map(item => (
              <div key={item.label} style={{
                flex: 1, background: item.bg, border: `1px solid ${item.border}`,
                borderRadius: 12, padding: '10px 8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: item.color }}>{String(item.value).padStart(2, '0')}</div>
                <div style={{ fontSize: 9, fontWeight: 700, color: item.color, letterSpacing: '0.5px', marginTop: 2 }}>{item.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {students.map((s, i) => (
              <StudentRow key={s.id} student={s} index={i} onChange={handleStatusChange} onRollClick={openDrawer} />
            ))}
          </div>
        </div>

        {/* 
          Bottom fade + button container.
          The gradient pseudo-layer fades the page bg color from transparent → solid,
          creating a reliable "frosted" look without needing backdropFilter at all.
        */}
        <div style={{
          position: 'absolute',
          left: 0, right: 0, bottom: 0,
          zIndex: 20,
          pointerEvents: 'none',
        }}>
          {/* Gradient fade layer */}
          <div style={{
            height: 48,
            background: 'linear-gradient(to bottom, rgba(253,252,254,0) 0%, rgba(253,252,254,1) 100%)',
            pointerEvents: 'none',
          }} />
          {/* Solid button area */}
          <div style={{
            background: '#FDFCFE',
            padding: '0 20px 32px',
            pointerEvents: 'all',
          }}>
            <SlideToSubmit
              onConfirm={handleSubmit}
              disabled={submitting || students.length === 0}
              label="Slide to confirm"
              confirmedLabel="Attendance Saved!"
            />
          </div>
        </div>

        {drawerStudent && <AttendanceDrawer
          student={drawerStudent}
          status={drawerStatus}
          onStatusChange={setDrawerStatus}
          onSave={saveDrawer}
          onClose={closeDrawer}
        />}
      </div>
    );
  }

  /* Class Select Screen */
  return (
    <div style={{
      minHeight: '100dvh', background: '#FDFCFE',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Inter', sans-serif",
    }}>
      <div style={{
        padding: '52px 20px 14px',
        display: 'flex', alignItems: 'center', gap: 12,
        borderBottom: `1px solid ${colors.borderLight}`,
        background: '#fff',
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 16, fontWeight: 500, color: colors.text,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          Student attendance
        </button>
      </div>

      <div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {MOCK_CLASSES.map((cls, i) => {
          const isMyClass = cls.id === teacher.classAssigned;
          return (
            <button key={cls.id} onClick={() => handleClassSelect(cls)} style={{
              width: '100%', padding: '16px 18px',
              background: isMyClass ? colors.primaryBg : '#fff',
              border: `1.5px solid ${isMyClass ? colors.borderLight : colors.border}`,
              borderRadius: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 14,
              textAlign: 'left',
              animation: `slideUp 0.25s ease ${i * 0.04}s both`,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: isMyClass ? '#EEE3F7' : '#f1f5f9',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke={isMyClass ? colors.primary : colors.textMuted}
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 00-3-3.87"/>
                  <path d="M16 3.13a4 4 0 010 7.75"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: isMyClass ? colors.primary : colors.text }}>
                    {cls.name} – Sec {cls.section}
                  </span>
                  {isMyClass && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, background: colors.primary,
                      color: '#fff', padding: '2px 8px', borderRadius: 20,
                    }}>MY CLASS</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 3 }}>{cls.studentCount} students</div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke={colors.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ClassAttendance;